import React, { Suspense } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { TodoAssigneesPopover } from "../../TodoEdit/TodoEditNewMain/Popovers/TodoAssigneesPopover/TodoAssigneesPopover";
import { RoutineOccasionEditAssigneesPopoverQuery } from "./__generated__/RoutineOccasionEditAssigneesPopoverQuery.graphql";

type Props = {
  routineId: string;
  sequenceNumber: number;
  storeId?: string;
  visible: boolean;
  onClose: () => void;
  disabled: boolean;
};

export function RoutineOccasionEditAssigneesPopover(props: Props) {
  return (
    <Suspense fallback={null}>
      <RoutineOccasionEditAssigneesPopoverInner {...props} />
    </Suspense>
  );
}

function RoutineOccasionEditAssigneesPopoverInner(props: Props) {
  const data = useLazyLoadQuery<RoutineOccasionEditAssigneesPopoverQuery>(
    graphql`
      query RoutineOccasionEditAssigneesPopoverQuery(
        $routineId: ID!
        $sequenceNumber: Int!
        $storeId: ID
        $visible: Boolean!
      ) {
        routine(id: $routineId) @include(if: $visible) {
          occasion(sequenceNumber: $sequenceNumber, storeId: $storeId) {
            assignableEmployees {
              ...TodoAssigneesPopover_EmployeeConnection
            }
            assigned {
              employee {
                id
              }
            }
            assignedRoles {
              role {
                id
                ...TodoAssigneesPopover_Roles
              }
            }
          }
        }
      }
    `,
    {
      routineId: props.routineId,
      sequenceNumber: props.sequenceNumber,
      storeId: props.storeId,
      visible: props.visible,
    },
    {
      fetchPolicy: "store-and-network",
    }
  );

  const [updateAssignees] = useMutation(graphql`
    mutation RoutineOccasionEditAssigneesPopoverSetAssigneesMutation(
      $routineId: ID!
      $sequenceNumber: Int!
      $employeeIds: [ID!]!
    ) {
      routine(id: $routineId) {
        occasion(sequenceNumber: $sequenceNumber) {
          updateAssignedEmployees(employeeIds: $employeeIds) {
            id
            assigned {
              employee {
                id
              }
            }
          }
        }
      }
    }
  `);

  if (!data.routine?.occasion?.assignableEmployees) {
    return null;
  }

  const occasion = data.routine.occasion;

  return (
    <TodoAssigneesPopover
      key={props.visible ? "visible" : "hidden"}
      employeeConnection={occasion.assignableEmployees}
      visible={props.visible}
      onClose={props.onClose}
      assignEmployeeIds={
        occasion.assigned?.flatMap((a) =>
          a.employee?.id ? [a.employee.id] : []
        ) ?? []
      }
      setAssignEmployeeIds={(selectedIds) => {
        updateAssignees({
          variables: {
            routineId: props.routineId,
            sequenceNumber: props.sequenceNumber,
            employeeIds: selectedIds,
          },
        });
      }}
      assignRoleIds={occasion.assignedRoles?.map((ar) => ar.role.id) ?? []}
      setAssignRoleIds={() => {}} // Roles are read-only on RoutineOccasion
      roles={occasion.assignedRoles.map((ar) => ar.role) ?? []}
      rolesLocked
      disabled={props.disabled}
    />
  );
}
